import type { Icon as IconType } from '@rover/icons';
import { Flex, Text } from '@rover/kibble/core';
import TextWithSingleInlineColor from '@rover/kibble/official-patterns/TextWithSingleInlineColor/TextWithSingleInlineColor';
import { isTaggedParagraphText } from '@rover/kibble/official-patterns/TextWithSingleInlineLink/TextWithSingleInlineLink.common';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import type { ThemeTextColor } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';

export type Props = {
  text: string;
  icon: IconType;
  iconFill?: string;
  highlightColor?: ThemeTextColor;
  ariaLabel?: string;
  dataTestId?: string;
};

/**
 * @param text The text to render, can optionally include <0></0> tags to get it
 * highlighted with highlightColor
 * @param icon The icon to render to the left of the text
 * @param iconFill Optional color for the icon, defaults to theme.textColor.primary
 * @param highlightColor Optional color for highlighted text, defaults to theme.textColor.primary
 * @param ariaLabel Optional aria-label for accessibility
 * @param dataTestId Optional data-testid for testing purposes
 */
const SitterHighlight: React.FC<Props> = ({
  icon: Icon,
  text,
  iconFill,
  highlightColor = 'primary',
  ariaLabel,
  dataTestId,
}: Props) => {
  const theme = useTheme();
  const textProps = {
    size: '200' as const,
    textColor: 'secondary' as const,
  };
  // When an ariaLabel is provided, announce it via visually-hidden real text and
  // hide the visible icon + text from assistive tech. aria-label on the inner
  // <span> is ignored by mobile screen readers (VoiceOver/TalkBack), so they
  // would otherwise only announce the visible text (e.g. the bare number).
  const hideFromScreenReaders = ariaLabel ? true : undefined;
  return (
    <Flex gap="2x" flexDirection="row" data-testid={dataTestId} alignItems="center">
      {ariaLabel && <A11yHiddenBox>{ariaLabel}</A11yHiddenBox>}
      <Icon
        height="20px"
        width="20px"
        fill={iconFill ?? theme.textColor.primary}
        aria-hidden={hideFromScreenReaders}
      />
      {isTaggedParagraphText(text) ? (
        <TextWithSingleInlineColor
          paragraphText={text}
          highlightColor={highlightColor}
          aria-hidden={hideFromScreenReaders}
          {...textProps}
        />
      ) : (
        <Text aria-hidden={hideFromScreenReaders} {...textProps}>
          {text}
        </Text>
      )}
    </Flex>
  );
};

export default SitterHighlight;
